/**
 * Tap-in to Ori — the contract every Ori entry point on a page uses.
 *
 * A surface registers four things: an entry-point id, a label, a message,
 * and the context the tap carries. The label is the same everywhere; the
 * message names where the user came from; the numbers ride in `context`
 * so Ori has them without showing them on screen.
 *
 * Mirrored by mobile's `components/ori/tapIn.ts` — change both.
 * Copy lives in `@wone/locales` (`components:askOri.*`).
 *
 * `context` is the wire shape Coach receives, hence the snake_case keys.
 * It keys everything on stable ids: display strings change with the
 * learner's language, so nothing downstream may key off them. The localised
 * domain name is passed separately and used only to word the message; a
 * content title travels beside its `content_id` for the message and for
 * analytics, and Coach never renders it.
 */

export const TAP_IN_ENTRY_POINTS = [
  "web_track_index",
  "web_home_index",
  "web_recording_detail",
  "web_live_session_detail",
  "post_recording_feedback",
  "discover_help_me_choose",
  "home_tailored_list",
] as const;

export type TapInEntryPoint = typeof TAP_IN_ENTRY_POINTS[number];

export type TapInSurface = "index" | "domain" | "biometric" | "content";

export interface TapInWearableTheme {
  /**
   * Theme shorthand, e.g. `theme_sleep_duration`. Some wearable themes share a
   * shorthand with an assessment theme, so it is unique only under `biometrics`.
   */
  theme_key: string;
  score: number;
}

export interface TapInBiometrics {
  readiness?: number;
  trend_7d?: "up" | "down" | "steady";
  stability?: number;
  /** The domain's wearable theme scores, so a hybrid domain carries both sources. */
  themes?: TapInWearableTheme[];
}

export interface TapInContent {
  content_id: string;
  title: string;
  pillar?: string;
  content_tags?: string[];
  matched_theme?: string;
}

export interface TapInContext {
  surface: TapInSurface;
  /** Index domain or biometric shorthand, e.g. `domain_sleep`, `domain_biometrics_hrv_readiness`. */
  domain_key?: string;
  /**
   * The latest score Platform holds for the surface: the Index score, or the
   * domain's current score (blended on a hybrid domain), which the Track tab
   * shows and the domain chart ends on. Mobile's domain detail card headlines
   * the chart window's average instead, so on that page this is not the
   * card's number. `previous_score` and `band_key` describe this reading.
   */
  score?: number;
  previous_score?: number;
  /** Band label key from Platform, camelCase as served (e.g. `worthWatching`) — never the translated label. */
  band_key?: string;
  biometrics?: TapInBiometrics;
  content?: TapInContent;
}

/** What the learner sees, used only to word the message. */
export interface TapInDisplay {
  domainName?: string;
}

// A type alias, not an interface: i18next's typed `t` needs options that
// satisfy an index signature, which interfaces never do.
export type TapInMessageValues = {
  domain?: string;
  title?: string;
};

/**
 * What a tap-in puts on the wire beside its message: the `entry_point` and
 * `context` fields of the user frame.
 */
export interface TapInWire {
  entryPoint: TapInEntryPoint;
  context: TapInContext;
}

export interface TapIn {
  entryPoint: TapInEntryPoint;
  labelKey: "components:askOri.label";
  messageKey: `components:askOri.message.${TapInSurface}`;
  messageValues: TapInMessageValues;
  context: TapInContext;
}

const requireValue = (
  value: string | undefined,
  field: string,
  surface: TapInSurface
): string => {
  if (!value) {
    throw new Error(`Tap-in on a "${surface}" surface needs ${field}`);
  }
  return value;
};

const messageValuesFor = (
  context: TapInContext,
  display: TapInDisplay
): TapInMessageValues => {
  switch (context.surface) {
    case "index":
      return {};
    case "domain":
    case "biometric":
      requireValue(context.domain_key, "context.domain_key", context.surface);
      return {
        domain: requireValue(
          display.domainName,
          "display.domainName",
          context.surface
        ),
      };
    case "content":
      requireValue(
        context.content?.content_id,
        "context.content.content_id",
        context.surface
      );
      return {
        title: requireValue(
          context.content?.title,
          "context.content.title",
          context.surface
        ),
      };
  }
};

export const buildTapIn = (
  entryPoint: TapInEntryPoint,
  context: TapInContext,
  display: TapInDisplay = {}
): TapIn => ({
  entryPoint,
  labelKey: "components:askOri.label",
  messageKey: `components:askOri.message.${context.surface}`,
  messageValues: messageValuesFor(context, display),
  context,
});
